import { logError } from '@/lib/sentry.lib';

import { useEffect, useRef, useState } from 'react';

import { getAttachmentFeatures } from '@/feature/attachment.feature';
import { selectAttachments } from '@repo/api-adapters/selectors/attachments.selectors';
import { selectOrderNoSource } from '@repo/api-adapters/selectors/customer-assets.selectors';
import { translate } from '@repo/phrase';
import type { AssetAttachment, DeleteAttachmentParams, UpdateAttachmentCommentParams } from '@repo/types/attachment';
import { AttachmentsList, UploadAttachment } from '@repo/ui/attachments';
import { formatDate, getCountryLanguage } from '@repo/utils';

import { useAssetActionButton } from '@/components/asset-details/asset-action-buttons/asset-action-buttons.hooks';
import { AttachmentCommentModal } from '@/components/attachments/attachment-comment-modal';
import { AttachmentDeleteModal } from '@/components/attachments/attachment-delete-modal';
import { LoadingPage } from '@/components/loading';
import { QueryError } from '@/components/query-error';

import { downloadAttachment } from '@/api/asset.api';
import {
    useAsset,
    useAssetParams,
    useDeleteAttachment,
    useUpdateAttachmentComment,
    useUploadAttachment,
} from '@/hooks/asset.hooks';
import { useResponsive } from '@/hooks/responsive.hooks';
import { getAppConfig } from '@/utils/window.utils';

interface Props {
    shouldOpenUploadDialog?: boolean;
    onUploadDialogRequestHandled?: () => void;
}

export const Attachments = ({ shouldOpenUploadDialog = false, onUploadDialogRequestHandled }: Props) => {
    const attachmentContentRef = useRef<HTMLDivElement>(null);
    const { assetType, assetId } = useAssetParams();
    const { data, isError, isLoading, error } = useAsset(assetType, assetId);
    const { mutate: uploadFile, isPending } = useUploadAttachment(assetType, assetId);
    const { mutate: deleteFile, isPending: isDeleting } = useDeleteAttachment(assetType, assetId);
    const { mutate: updateComment, isPending: isCommentSaving } = useUpdateAttachmentComment(assetType, assetId);
    const { isButtonDisabled, isModificationButtonDisabled } = useAssetActionButton();
    const isAssetActionDisabled = isModificationButtonDisabled || isButtonDisabled;
    const isUploadDisabled = isAssetActionDisabled || isPending;
    const { isDesktop } = useResponsive();
    const attachments = selectAttachments(data);
    const { country } = getCountryLanguage();
    const orderNoSourceResult = selectOrderNoSource(data);
    const orderNoSource =
        typeof orderNoSourceResult === 'function' ? orderNoSourceResult(country) : orderNoSourceResult;
    const config = getAppConfig();
    const features = getAttachmentFeatures(assetType);
    const { isAddEnabled, isEditEnabled, isRemoveEnabled, isCommentEnabled } = isAssetActionDisabled
        ? { isAddEnabled: false, isEditEnabled: false, isRemoveEnabled: false, isCommentEnabled: false }
        : features;
    const [pendingDelete, setPendingDelete] = useState<AssetAttachment>();
    const hasPendingDelete = Boolean(pendingDelete);
    const [pendingComment, setPendingComment] = useState<AssetAttachment>();
    const hasPendingComment = Boolean(pendingComment);

    useEffect(() => {
        if (!shouldOpenUploadDialog || isLoading || isError || !isAddEnabled) {
            return;
        }

        const animationFrame = requestAnimationFrame(() => {
            const input = attachmentContentRef.current?.querySelector<HTMLInputElement>(
                '[data-ot-attachment-upload-input]'
            );
            input?.click();
            onUploadDialogRequestHandled?.();
        });

        return () => {
            cancelAnimationFrame(animationFrame);
        };
    }, [isAddEnabled, isError, isLoading, onUploadDialogRequestHandled, shouldOpenUploadDialog]);

    if (isError && !data) {
        return <QueryError error={error} />;
    }
    if (isLoading) {
        return <LoadingPage />;
    }

    const handleFileSelect = (file: File) => {
        if (isUploadDisabled) {
            return;
        }
        if (!orderNoSource) {
            logError(new Error('Missing order source'));
            return;
        }
        uploadFile({ file, orderNumber: assetId, orderNoSource });
    };

    const handleDelete = (attachment: AssetAttachment) => {
        setPendingDelete(attachment);
    };

    const handleCloseComment = () => {
        setPendingComment(undefined);
    };

    const handleConfirmComment = (comment: string) => {
        if (isCommentSaving) {
            return;
        }
        if (!pendingComment?.fileReference || !pendingComment.attachmentLocation || !orderNoSource) {
            logError(new Error('Missing required fields for comment update'));
            setPendingComment(undefined);
            return;
        }
        const { fileReference, attachmentLocation } = pendingComment;
        const params: UpdateAttachmentCommentParams = {
            fileReference,
            attachmentLocation,
            attachmentComment: comment,
            orderNo: assetId,
            orderNoSource,
        };
        updateComment(params, {
            onSuccess: () => {
                setPendingComment(undefined);
            },
        });
    };

    const handleConfirmDelete = () => {
        if (isDeleting) {
            return;
        }
        if (pendingDelete?.fileReference && orderNoSource) {
            const { fileReference, attachmentLocation } = pendingDelete;
            const params: DeleteAttachmentParams = {
                fileReference,
                attachmentLocation,
                orderNo: assetId,
                orderNoSource,
            };
            deleteFile(params, {
                onSuccess: () => {
                    setPendingDelete(undefined);
                },
            });
        }
    };
    return (
        <div ref={attachmentContentRef}>
            {isAddEnabled && (
                <UploadAttachment
                    onFileSelect={handleFileSelect}
                    isDesktop={isDesktop}
                    accept={config.feature.attachments?.add?.acceptedFileTypes ?? '*'}
                    disabled={isUploadDisabled}
                    translate={translate}
                    loading={isPending}
                />
            )}
            <AttachmentsList
                attachments={attachments}
                translate={translate}
                formatDateTime={(date: string) => formatDate(date, config.dateFormat)}
                downloadAttachment={downloadAttachment}
                logError={logError}
                isEditEnabled={isEditEnabled}
                {...(isRemoveEnabled ? { onDelete: handleDelete } : {})}
                {...(isCommentEnabled ? { onComment: setPendingComment } : {})}
            />
            <AttachmentDeleteModal
                visible={hasPendingDelete}
                onClose={() => {
                    setPendingDelete(undefined);
                }}
                onConfirm={handleConfirmDelete}
                isPending={isDeleting}
            />
            <AttachmentCommentModal
                key={pendingComment?.fileReference}
                visible={hasPendingComment}
                onClose={handleCloseComment}
                attachmentComment={pendingComment?.attachmentComment ?? ''}
                onConfirm={handleConfirmComment}
                isPending={isCommentSaving}
            />
        </div>
    );
};
